import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';

import axios from 'axios';

import {
    Card,
    Col,
    DatePicker,
    Input,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message,
    Button
} from 'antd';

import {
    HistoryOutlined,
    ReloadOutlined,
    SearchOutlined
} from '@ant-design/icons';

import dayjs from 'dayjs';

import Layout from './Layout';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const EVENT_LABELS = {
    login: {
        text: 'Вход',
        color: 'green'
    },
    login_failed: {
        text: 'Неудачный вход',
        color: 'red'
    },
    profile_update: {
        text: 'Изменение данных',
        color: 'blue'
    }
};

const FIELD_LABELS = {
    phone: 'Телефон',
    email: 'Email',
    password: 'Пароль',
    login: 'Логин',
    last_name: 'Фамилия',
    first_name: 'Имя',
    middle_name: 'Отчество',
    role_id: 'Роль',
    external_id: 'Внешний ID',
    counterparty_ids: 'Контрагенты'
};

const formatValue = value => {
    if (
        value === null ||
        value === undefined ||
        value === ''
    ) {
        return '—';
    }

    if (Array.isArray(value)) {
        return value.length
            ? value.join(', ')
            : '—';
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return String(value);
};

const formatChanges = details => {
    const changes =
        details?.changes;

    if (
        !Array.isArray(changes) ||
        !changes.length
    ) {
        return '—';
    }

    return changes.map(change => {
        const label =
            FIELD_LABELS[change.field] ||
            change.field;

        if (
            change.field === 'password' ||
            change.changed
        ) {
            return `${label}: изменён`;
        }

        return `${label}: ${formatValue(change.from)} → ${formatValue(change.to)}`;
    }).join('; ');
};

const UserActivityLogsPage = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [eventType, setEventType] = useState('all');
    const [login, setLogin] = useState('');
    const [searchLogin, setSearchLogin] = useState('');
    const [dateRange, setDateRange] = useState(null);

    const fetchLogs = useCallback(async () => {
        setLoading(true);

        try {
            const token =
                localStorage.getItem('token');

            const params = {
                page,
                limit: pageSize,
                event_type: eventType,
                login: searchLogin || undefined,
                date_from:
                    dateRange?.[0]
                        ? dateRange[0].format('YYYY-MM-DD')
                        : undefined,
                date_to:
                    dateRange?.[1]
                        ? dateRange[1].format('YYYY-MM-DD')
                        : undefined
            };

            const response = await axios.get(
                '/api/user-activity-logs',
                {
                    params,
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setRows(response.data?.data || []);
            setTotal(response.data?.total || 0);
        } catch (error) {
            console.error(error);
            message.error(
                error.response?.data?.message ||
                    'Не удалось загрузить журнал'
            );
        } finally {
            setLoading(false);
        }
    }, [
        page,
        pageSize,
        eventType,
        searchLogin,
        dateRange
    ]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const columns = useMemo(
        () => [
            {
                title: 'Когда',
                dataIndex: 'created_at',
                width: 170,
                render: value =>
                    value
                        ? dayjs(value).format(
                              'DD.MM.YYYY HH:mm:ss'
                          )
                        : '—'
            },
            {
                title: 'Событие',
                dataIndex: 'event_type',
                width: 150,
                render: type => {
                    const meta =
                        EVENT_LABELS[type] || {
                            text: type,
                            color: 'default'
                        };

                    return (
                        <Tag color={meta.color}>
                            {meta.text}
                        </Tag>
                    );
                }
            },
            {
                title: 'Пользователь',
                key: 'user',
                width: 220,
                render: (_, record) => {
                    const fullName = [
                        record.user?.last_name,
                        record.user?.first_name
                    ]
                        .filter(Boolean)
                        .join(' ');

                    return (
                        <Space direction="vertical" size={0}>
                            <Text strong>
                                {record.login || '—'}
                            </Text>
                            {fullName ? (
                                <Text type="secondary">
                                    {fullName}
                                </Text>
                            ) : null}
                        </Space>
                    );
                }
            },
            {
                title: 'IP',
                dataIndex: 'ip',
                width: 140,
                render: value => value || '—'
            },
            {
                title: 'Детали',
                key: 'details',
                render: (_, record) => {
                    if (
                        record.event_type ===
                        'profile_update'
                    ) {
                        const actor =
                            record.details
                                ?.actor_login;

                        const source =
                            record.details
                                ?.source ===
                            'admin_users'
                                ? 'админ'
                                : record.details
                                        ?.source ===
                                    'customer_cabinet'
                                  ? 'кабинет заказчика'
                                  : null;

                        return (
                            <Space
                                direction="vertical"
                                size={2}
                            >
                                <Text>
                                    {formatChanges(
                                        record.details
                                    )}
                                </Text>
                                {actor || source ? (
                                    <Text type="secondary">
                                        {[
                                            source
                                                ? `Источник: ${source}`
                                                : null,
                                            actor
                                                ? `Кто изменил: ${actor}`
                                                : null
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </Text>
                                ) : null}
                            </Space>
                        );
                    }

                    if (
                        record.event_type ===
                        'login_failed'
                    ) {
                        return (
                            <Text type="secondary">
                                {record.details
                                    ?.reason ===
                                'invalid_password'
                                    ? 'Неверный пароль'
                                    : record.details
                                            ?.reason ===
                                        'user_not_found'
                                      ? 'Пользователь не найден'
                                      : 'Ошибка входа'}
                            </Text>
                        );
                    }

                    return (
                        <Text type="secondary">
                            {record.user_agent
                                ? String(
                                      record.user_agent
                                  ).slice(0, 80)
                                : '—'}
                        </Text>
                    );
                }
            }
        ],
        []
    );

    return (
        <Layout>
            <div style={{ padding: 24 }}>
                <Space
                    style={{
                        marginBottom: 16,
                        width: '100%',
                        justifyContent: 'space-between'
                    }}
                    wrap
                >
                    <Space>
                        <HistoryOutlined
                            style={{ fontSize: 22 }}
                        />
                        <Title
                            level={3}
                            style={{ margin: 0 }}
                        >
                            Журнал доступа
                        </Title>
                    </Space>

                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchLogs}
                        loading={loading}
                    >
                        Обновить
                    </Button>
                </Space>

                <Card style={{ marginBottom: 16 }}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={6}>
                            <Select
                                style={{ width: '100%' }}
                                value={eventType}
                                onChange={value => {
                                    setPage(1);
                                    setEventType(value);
                                }}
                                options={[
                                    {
                                        value: 'all',
                                        label: 'Все события'
                                    },
                                    {
                                        value: 'auth',
                                        label: 'Авторизации'
                                    },
                                    {
                                        value: 'changes',
                                        label: 'Изменения данных'
                                    },
                                    {
                                        value: 'login',
                                        label: 'Успешный вход'
                                    },
                                    {
                                        value: 'login_failed',
                                        label: 'Неудачный вход'
                                    },
                                    {
                                        value: 'profile_update',
                                        label: 'Изменение профиля'
                                    }
                                ]}
                            />
                        </Col>

                        <Col xs={24} md={6}>
                            <Input.Search
                                allowClear
                                placeholder="Логин"
                                value={login}
                                onChange={e =>
                                    setLogin(e.target.value)
                                }
                                onSearch={value => {
                                    setPage(1);
                                    setSearchLogin(
                                        value.trim()
                                    );
                                }}
                                enterButton={
                                    <SearchOutlined />
                                }
                            />
                        </Col>

                        <Col xs={24} md={8}>
                            <RangePicker
                                style={{ width: '100%' }}
                                value={dateRange}
                                onChange={value => {
                                    setPage(1);
                                    setDateRange(value);
                                }}
                                format="DD.MM.YYYY"
                            />
                        </Col>
                    </Row>
                </Card>

                <Card>
                    <Table
                        rowKey="id"
                        loading={loading}
                        columns={columns}
                        dataSource={rows}
                        scroll={{ x: 1000 }}
                        pagination={{
                            current: page,
                            pageSize,
                            total,
                            showSizeChanger: true,
                            pageSizeOptions: [
                                '20',
                                '50',
                                '100'
                            ],
                            showTotal: value =>
                                `Всего: ${value}`,
                            onChange: (
                                nextPage,
                                nextSize
                            ) => {
                                setPage(nextPage);
                                setPageSize(nextSize);
                            }
                        }}
                    />
                </Card>
            </div>
        </Layout>
    );
};

export default UserActivityLogsPage;
